import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FAQSection = ({ faqs = [] }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  const groupedFaqs = faqs.reduce((acc, faq) => {
    const category = faq.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(faq);
    return acc;
  }, {});

  const categories = Object.keys(groupedFaqs);

  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-amber-500/5 rounded-full filter blur-3xl opacity-20 -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-amber-500/5 rounded-full filter blur-3xl opacity-20 translate-y-1/2" />

      <div className="max-w-4xl mx-auto px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 text-lg">
            Find answers to common questions about Ceylon Broker
          </p>
        </motion.div>

        {faqs && faqs.length > 0 ? (
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="group"
              >
                <motion.button
                  onClick={() => setActiveIndex(activeIndex === idx ? null : idx)}
                  className="w-full relative"
                  whileHover={{ x: 4 }}
                >
                  {/* Glass background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-amber-500/0 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  
                  <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-xl px-8 py-5 border border-amber-500/20 group-hover:border-amber-500/40 transition-all duration-300 flex justify-between items-center">
                    <h3 className="text-left font-semibold text-white text-lg">
                      {faq.question}
                    </h3>
                    <motion.div
                      animate={{ rotate: activeIndex === idx ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-amber-400 flex-shrink-0 ml-4"
                    >
                      ▼
                    </motion.div>
                  </div>
                </motion.button>

                {/* Answer */}
                <AnimatePresence>
                  {activeIndex === idx && (
                    <motion.div
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl px-8 py-6 border border-amber-500/20 text-gray-300 leading-relaxed">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-gray-400"
          >
            <p>No FAQs available yet. Check back soon!</p>
          </motion.div>
        )}

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-400 mb-4">Still have questions?</p>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(251, 191, 36, 0.2)' }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold rounded-lg hover:from-amber-300 hover:to-amber-400 transition-all duration-300"
          >
            Contact Our Support Team
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
